import { LoginToWebChatSystem, RefreshWebChatToken } from './api';
import { API_URLS } from '../constants/apiUrls';
import { AUTH_FLOWS, ONE_SECOND_MS, SESSION_TIMING } from '../constants/Session';
import { getParentOriginFromUrl, cacheToken } from '../utils/authUtils';
import { toMillis, getJwtExpiryMs } from '../utils/tokenExpiry';

// One renewer per way of getting a fresh access token. Each says whether it
// can renew the given auth data and, when asked, resolves to the fields that
// change with the fresh token; the caller merges them into the current auth
// data. All of them swap the token in place, without reloading the page, so
// nothing the user is doing is interrupted.

// Relative expiresIn (seconds from now) as epoch milliseconds.
const fromExpiresIn = (expiresIn) =>
  Number(expiresIn) > 0 ? Date.now() + Number(expiresIn) * ONE_SECOND_MS : null;

// Absolute access token expiry in epoch seconds. The refresh endpoint sends
// accessTokenExpiresAt (the same instant as the JWT exp); relative expiresIn
// and the JWT exp claim itself are fallbacks. With none of them the expiry
// stays unknown rather than "now"; the caller rejects such a renewal.
const toExpiresAtSeconds = ({ AccessToken, AccessTokenExpiresAt, ExpiresIn }) => {
  const expiresAtMs =
    toMillis(AccessTokenExpiresAt) ?? fromExpiresIn(ExpiresIn) ?? getJwtExpiryMs(AccessToken);
  if (expiresAtMs === null) {
    console.warn('Session renewal response has no readable expiry; the caller will reject it');
    return null;
  }
  return Math.floor(expiresAtMs / ONE_SECOND_MS);
};

const isFlow = (authData, flow) => authData?.authFlow === flow;

// Chat link flow: rotate the refresh token via POST /api/v2/auth/refresh.
// The server may omit a new refresh token, in which case the current one is
// kept.
const refreshTokenRenewer = {
  canRenew: (authData) =>
    isFlow(authData, AUTH_FLOWS.CHAT_LINK) &&
    Boolean(authData.refreshToken && authData.eventId && authData.accountId1 && authData.clientId),

  renew: async (authData) => {
    const renewed = await RefreshWebChatToken({
      url: API_URLS.REFRESH,
      refreshToken: authData.refreshToken,
      eventId: authData.eventId,
      accountId: authData.accountId1,
      clientId: authData.clientId,
    });
    return {
      accessToken: renewed.AccessToken,
      systemToken: renewed.AccessToken,
      refreshToken: renewed.RefreshToken || authData.refreshToken,
      accessTokenExpiresAt: toExpiresAtSeconds(renewed),
    };
  },
};

// Chat link flow, when the refresh token is missing, revoked or expired: log
// in again with the guard token from the chat link, the same call AuthGuard
// makes on page load. Brings a new refresh token too. The domain comes from
// authData (seeded by AuthGuard via useAppEnv) rather than process.env so
// this service module does not read env directly and stays consistent with
// the hook-driven env context the rest of the auth flow uses.
const guardLoginRenewer = {
  canRenew: (authData) =>
    isFlow(authData, AUTH_FLOWS.CHAT_LINK) && Boolean(authData._guard_ && authData.domain),

  renew: async (authData) => {
    const login = await LoginToWebChatSystem({
      url: API_URLS.LOGIN,
      data: authData._guard_,
      domain: authData.domain,
    });
    if (!login?.AccessToken) throw new Error('Login with the chat link token returned no access token');
    return {
      accessToken: login.AccessToken,
      systemToken: login.AccessToken,
      refreshToken: login.RefreshToken || null,
      accessTokenExpiresAt: toExpiresAtSeconds(login),
    };
  },
};

// Ask the parent eventscribe window for a fresh getSession.asp token and
// resolve with its answer. Only messages from the parent origin count.
// Rejects as soon as the parent answers with an error (its website session
// ended), when it does not answer in time, or when postMessage throws; the
// listener and timeout are torn down on every outcome.
const requestTokenFromParent = (parentOrigin, accountIdTo) =>
  new Promise((resolve, reject) => {
    let responseTimeout = null;
    let messageListener = null;

    const teardown = () => {
      clearTimeout(responseTimeout);
      window.removeEventListener('message', messageListener);
    };

    messageListener = (event) => {
      if (event.origin !== parentOrigin) return;
      if (event.data?.action === 'error') {
        teardown();
        reject(new Error(`Parent window refused token refresh: ${event.data.message || 'no reason given'}`));
        return;
      }
      if (event.data?.action !== 'token' || !event.data.token) return;
      teardown();
      resolve(event.data);
    };

    window.addEventListener('message', messageListener);
    responseTimeout = setTimeout(() => {
      teardown();
      reject(new Error('Parent window did not answer token refresh request'));
    }, SESSION_TIMING.RENEWAL_TIMEOUT_MS);

    try {
      window.opener.postMessage({ action: 'requestToken', accountIdTo: accountIdTo || '' }, parentOrigin);
    } catch (error) {
      teardown();
      reject(error);
    }
  });

// Website flow: the token comes from the parent window, which keeps a
// permanent listener while the website session is alive. Only applies when
// the token carries its expires (getSession.asp
// always sends it; without it there is no timeline to renew against) and the
// opener window is still open. The new token is cached the same way the
// initial handover is.
const parentWindowRenewer = {
  canRenew: (authData) =>
    isFlow(authData, AUTH_FLOWS.WEBSITE) &&
    Boolean(authData.expires && window.opener && !window.opener.closed),

  renew: async (authData) => {
    const parentOrigin = getParentOriginFromUrl();
    if (!parentOrigin) throw new Error('Parent origin unknown');

    const tokenData = await requestTokenFromParent(parentOrigin, authData.accountId2);
    window.__authTokenData__ = tokenData;
    cacheToken(tokenData);
    return {
      accessToken: tokenData.token,
      systemToken: tokenData.token,
      expires: tokenData.expires,
    };
  },
};

// Order matters: the cheapest renewal first, re-login as the fallback. The
// website flow has only the parent window: without it no fresh token can be
// had (reloading would only bring back the same cached token).
const RENEWERS = [refreshTokenRenewer, guardLoginRenewer, parentWindowRenewer];

// Every renewer that applies to this auth data, to try in order. Empty when
// nothing can renew it.
export const getSessionRenewers = (authData) =>
  RENEWERS.filter((renewer) => renewer.canRenew(authData));
